import { useMemo, useCallback } from 'react';
import { HostingType, TransferMethod } from '../types/connection';
import { TransferDirection } from '../types/task';

/**
 * T079: Auto-suggest name generation hook for connections and tasks
 *
 * For connections: "{partnerName} {method} {hostingType}"
 * Example: "Acme SFTP Hosted"
 *
 * For tasks: "{connectionName} {direction}"
 * Example: "Acme SFTP Hosted - Inbound"
 */

export type AutoSuggestType = 'connection' | 'task';

export interface ConnectionSuggestContext {
  partnerName?: string;
  transferMethod?: TransferMethod;
  hostingType?: HostingType;
}

export interface TaskSuggestContext {
  connectionName?: string;
  transferDirection?: TransferDirection;
}

export type AutoSuggestContext = ConnectionSuggestContext | TaskSuggestContext;

export interface UseAutoSuggestNameOptions {
  type: AutoSuggestType;
}

export interface UseAutoSuggestNameReturn {
  /** Generate a suggested name based on the provided context */
  generateSuggestedName: (context: AutoSuggestContext) => string;
  /** Check if all required context fields are present for name generation */
  hasRequiredContext: (context: AutoSuggestContext) => boolean;
}

/**
 * Format hosting type for display
 */
function formatHostingType(hostingType?: HostingType): string {
  if (!hostingType) return '';
  return hostingType === 'hosted' ? 'Hosted' : 'Remote';
}

/**
 * Format transfer method for display
 */
function formatTransferMethod(method?: TransferMethod): string {
  if (!method) return '';
  switch (method) {
    case 'sftp':
      return 'SFTP';
    case 'ftp':
      return 'FTP';
    case 's3':
      return 'S3';
    case 'api':
      return 'API';
    case 'azure_blob':
      return 'Azure';
  }
  // This should never be reached, but TypeScript needs it for exhaustiveness
  return method;
}

/**
 * Format transfer direction for display
 */
function formatTransferDirection(direction?: TransferDirection): string {
  if (!direction) return '';
  switch (direction) {
    case 'inbound':
      return 'Inbound';
    case 'outbound':
      return 'Outbound';
    default:
      return direction;
  }
}

/**
 * Check if context is for connection type
 */
function isConnectionContext(context: AutoSuggestContext): context is ConnectionSuggestContext {
  return 'partnerName' in context || 'transferMethod' in context || 'hostingType' in context;
}

/**
 * Check if context is for task type
 */
function isTaskContext(context: AutoSuggestContext): context is TaskSuggestContext {
  return 'connectionName' in context || 'transferDirection' in context;
}

/**
 * Hook for generating auto-suggested names for connections and tasks
 *
 * @example
 * ```tsx
 * // For connections
 * const { generateSuggestedName, hasRequiredContext } = useAutoSuggestName({ type: 'connection' });
 * const suggestedName = generateSuggestedName({
 *   partnerName: 'Acme Corp',
 *   transferMethod: 'sftp',
 *   hostingType: 'hosted'
 * });
 * // Returns: "Acme Corp SFTP Hosted"
 *
 * // For tasks
 * const { generateSuggestedName, hasRequiredContext } = useAutoSuggestName({ type: 'task' });
 * const suggestedName = generateSuggestedName({
 *   connectionName: 'Acme Corp SFTP Hosted',
 *   transferDirection: 'inbound'
 * });
 * // Returns: "Acme Corp SFTP Hosted - Inbound"
 * ```
 */
export function useAutoSuggestName({ type }: UseAutoSuggestNameOptions): UseAutoSuggestNameReturn {
  const generateConnectionName = useCallback((context: ConnectionSuggestContext): string => {
    const { partnerName, transferMethod, hostingType } = context;

    const parts: string[] = [];

    if (partnerName?.trim()) {
      parts.push(partnerName.trim());
    }

    if (transferMethod) {
      parts.push(formatTransferMethod(transferMethod));
    }

    if (hostingType) {
      parts.push(formatHostingType(hostingType));
    }

    return parts.join(' ');
  }, []);

  const generateTaskName = useCallback((context: TaskSuggestContext): string => {
    const { connectionName, transferDirection } = context;

    const parts: string[] = [];

    if (connectionName?.trim()) {
      parts.push(connectionName.trim());
    }

    if (transferDirection) {
      // Use a separator between connection name and direction
      if (parts.length > 0) {
        parts.push('-');
      }
      parts.push(formatTransferDirection(transferDirection));
    }

    return parts.join(' ');
  }, []);

  const generateSuggestedName = useCallback((context: AutoSuggestContext): string => {
    if (type === 'connection' && isConnectionContext(context)) {
      return generateConnectionName(context);
    }

    if (type === 'task' && isTaskContext(context)) {
      return generateTaskName(context);
    }

    return '';
  }, [type, generateConnectionName, generateTaskName]);

  const hasRequiredContext = useCallback((context: AutoSuggestContext): boolean => {
    if (type === 'connection' && isConnectionContext(context)) {
      const { partnerName, transferMethod, hostingType } = context;
      return Boolean(partnerName?.trim() && transferMethod && hostingType);
    }

    if (type === 'task' && isTaskContext(context)) {
      const { connectionName, transferDirection } = context;
      return Boolean(connectionName?.trim() && transferDirection);
    }

    return false;
  }, [type]);

  return useMemo(() => ({
    generateSuggestedName,
    hasRequiredContext,
  }), [generateSuggestedName, hasRequiredContext]);
}

export default useAutoSuggestName;
